<script lang="ts">
    import type { Snippet } from "svelte";
    import { getContext } from "svelte";
    import type { Action } from "svelte/action";
    import HelpTooltip from "../Tooltip/HelpTooltip.svelte";
    import { helpTextDisabledStore } from "../../Stores/MenuStore";
    import { createFloatingUiActions } from "../../Utils/svelte-floatingui";

    interface Props {
        label?: string;
        tooltipTitle?: string;
        tooltipDesc?: string;
        disabledHelp?: boolean;
        tooltipDelay?: number;
        state?: "normal" | "active" | "forbidden" | "disabled" | "disabledForbidden";
        dataTestId?: string;
        classList?: string;
        hideIconInActionBar?: boolean;
        bgColor?: string;
        textColor?: string;
        isGradient?: boolean;
        hasImage?: boolean;
        action?: Action;
        onclick?: (event: MouseEvent) => void;
        onkeydown?: (event: KeyboardEvent) => void;
        onmouseenter?: (event: MouseEvent) => void;
        onmouseleave?: (event: MouseEvent) => void;
        media?: string;
        desc?: string;
        tooltipShortcuts?: string[];
        boldLabel?: boolean;
        wrapperDiv?: HTMLElement;
        // By default, the button will have a rounded corner on the left if it is the first of a div.
        // This behaviour can be overridden by setting the "first" prop to true or false explicitly.
        // Used for the responsive menu items.
        first?: boolean;
        // By default, the button will have a rounded corner on the right if it is the last of a div.
        // This behaviour can be overridden by setting the "last" prop to true or false explicitly.
        // Used for the responsive menu items.
        last?: boolean;
        // Can be used to force the context. If not set, the context is deduced from the "inMenu" Svelte context.
        context?: "actionBar" | "menu";
        children?: Snippet;
        tooltip?: Snippet;
        end?: Snippet;
    }

    let {
        label = undefined,
        tooltipTitle = "",
        tooltipDesc = "",
        disabledHelp = false,
        tooltipDelay = 500,
        state: buttonState = "normal",
        dataTestId = undefined,
        classList = "group",
        hideIconInActionBar = false,
        bgColor = undefined,
        textColor = undefined,
        isGradient = false,
        hasImage = true,
        action = () => {},
        onclick = undefined,
        onkeydown = undefined,
        onmouseenter = undefined,
        onmouseleave = undefined,
        media = "",
        desc = "",
        tooltipShortcuts = [],
        boldLabel = false,
        wrapperDiv = $bindable(),
        first = undefined,
        last = undefined,
        context = undefined,
        children,
        tooltip,
        end,
    }: Props = $props();

    let isInMenu = $derived(context !== undefined ? context === "menu" : getContext("inMenu"));

    let helpActive = $state(false);
    const [tooltipReference, tooltipContent] = createFloatingUiActions({ placement: "top" }, 8);

    function handleClick(event: MouseEvent) {
        if (buttonState === "disabled" || buttonState === "disabledForbidden") {
            return;
        }
        helpActive = false;
        onclick?.(event);
    }

    let styleVars = $derived(
        [bgColor ? `--bg-color: ${bgColor};` : "", textColor ? `--text-color: ${textColor};` : ""]
            .filter(Boolean)
            .join(" "),
    );
</script>

{#if !isInMenu}
    <div
        class={[
            "relative pointer-events-auto",
            classList,
            {
                "first-of-type:rounded-s-lg": first === undefined,
                "last-of-type:rounded-e-lg": last === undefined,
                "rounded-s-lg": first === true,
                "rounded-e-lg": last === true,
            },
        ]}
        use:action
        use:tooltipReference
        style={styleVars}
        bind:this={wrapperDiv}
    >
        <button
            type="button"
            class="nutrivera-action-button relative m-0 flex h-10 min-w-10 flex-none select-none items-center justify-center gap-1 rounded-[10px] p-1 outline-none transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--nutrivera-shell-accent)]
                    {buttonState === 'disabled' ? 'opacity-50 cursor-not-allowed' : ''}
                    {buttonState === 'disabledForbidden'
                ? 'bg-[var(--nutrivera-shell-danger)] opacity-70 cursor-not-allowed'
                : ''}
                    {buttonState === 'normal'
                ? 'bg-[var(--nutrivera-shell-raised)] hover:bg-[var(--nutrivera-shell-hover)] cursor-pointer'
                : ''}
                    {buttonState === 'active' ? 'bg-[var(--nutrivera-shell-accent)] cursor-pointer' : ''}
                    {buttonState === 'forbidden' ? 'bg-[var(--nutrivera-shell-danger)] cursor-pointer' : ''}
                    {!label ? '' : 'w-auto px-2.5 text-sm whitespace-nowrap'}
                    {isGradient ? 'gradient overflow-hidden font-bold' : ''}
                    {bgColor && buttonState === 'normal' ? 'bg-[var(--bg-color)]' : ''}
                    {textColor
                ? 'text-[var(--text-color)]'
                : buttonState === 'active'
                  ? 'text-[var(--nutrivera-shell-accent-ink)]'
                  : buttonState === 'forbidden' || buttonState === 'disabledForbidden'
                    ? 'text-white'
                    : 'text-[var(--nutrivera-shell-text)]'}"
            disabled={buttonState === "disabled" || buttonState === "disabledForbidden"}
            onclick={(event) => {
                event.preventDefault();
                handleClick(event);
            }}
            {onkeydown}
            onmouseenter={(event) => {
                helpActive = true;
                onmouseenter?.(event);
            }}
            onmouseleave={(event) => {
                helpActive = false;
                onmouseleave?.(event);
            }}
            data-testid={dataTestId}
        >
            {#if !hideIconInActionBar}
                {@render children?.()}
            {/if}
            {#if label}<span class={boldLabel ? "font-bold" : ""}>{label}</span>{/if}
        </button>
        {#if helpActive && !$helpTextDisabledStore && !disabledHelp && (tooltipTitle || tooltipDesc || tooltip)}
            <HelpTooltip
                title={tooltipTitle}
                helpMedia={media}
                {desc}
                shortcuts={tooltipShortcuts}
                delayBeforeAppear={tooltipDelay}
                floatingAction={tooltipContent}
            >
                {@render tooltip?.()}
            </HelpTooltip>
        {/if}
    </div>
{:else}
    <button
        class="nutrivera-action-button group flex p-2 gap-2 mb-1 items-center hover:bg-white/10 transition-all cursor-pointer font-bold text-sm text-neutral-100 w-full pointer-events-auto text-start rounded select-none
                    {buttonState === 'disabled' ? 'opacity-50 cursor-not-allowed' : ''}
                    {buttonState === 'disabledForbidden' ? 'bg-danger opacity-70 cursor-not-allowed' : ''}
                    {buttonState === 'active' && !isGradient
            ? 'bg-secondary hover:bg-secondary-600 cursor-pointer'
            : ''}
                    {buttonState === 'forbidden' ? 'bg-danger hover:bg-danger-600 cursor-pointer' : ''}
                    {isGradient ? 'gradient overflow-hidden' : ''}
                    {bgColor && !isGradient ? 'bg-[var(--bg-color)]' : ''}
                    {textColor ? 'text-[var(--text-color)]' : 'text-neutral-100'}
                    {isGradient ? 'relative' : ''}"
        use:action
        onclick={handleClick}
        {onkeydown}
        {onmouseenter}
        {onmouseleave}
        style={styleVars}
        data-testid={dataTestId}
        bind:this={wrapperDiv}
    >
        {#if hasImage}
            <div class="transition-all w-6 h-6 aspect-square text-center flex items-center justify-center">
                {@render children?.()}
            </div>
        {/if}
        <div class="text-start h-6 leading-4 flex items-center text-nowrap">
            {label ?? tooltipTitle ?? ""}
            {@render end?.()}
        </div>
    </button>
{/if}

<style>
    .gradient {
        background-color: var(--bg-color);
        color: var(--text-color, white);
    }
</style>
